import * as React from "react";
import type { DrawerSelectEvent, MenuSelectEvent } from "@progress/kendo-react-layout";
import { Avatar, Drawer, DrawerContent, Menu, MenuItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { useNavigate, useLocation } from "react-router-dom";
import { userIcon, logoutIcon, menuIcon } from "@progress/kendo-svg-icons";
import type { SidebarProps as SidebarProperties } from "./SidebarProps";
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import "./styles.css";
import { useEffect } from "react";
import { AuthService } from "@infrastructure/services";
import { useInjection } from "@presentation/components/dependencyInjection";
import { logOutInProgress } from "@presentation/components/helpers/constants";
import { ProfileWindow } from "./ProfileWindow"; // ← adjust path if needed
import { useShallow } from "zustand/react/shallow";

const Sidebar: React.FC<SidebarProperties> = (properties) => {
  const { setSessionState, username } = useUserManagementStore(
    useShallow((state) => ({
      setSessionState: state.setSessionState,
      username: state.userSession?.username ?? "Undefined",
    })),
  );

  const navigate = useNavigate();
  const authService = useInjection(AuthService);

  const [selectedIndex, setSelectedIndex] = React.useState<number>(() => {
    const jobListIndex = properties.routes.findIndex((route) => route.text === "Job List");
    const savedIndex = localStorage.getItem("selectedIndexSidebarIndex");
    if (savedIndex) {
      const parsed = Number(savedIndex);
      if (Number.isFinite(parsed)) return parsed;
    }
    return Math.max(jobListIndex, 0);
  });

  const [isExpanded, setIsExpanded] = React.useState<boolean>(localStorage.getItem("sidebarisExpanded") === "true");
  const [showProfileWindow, setShowProfileWindow] = React.useState<boolean>(false);

  const selectedRouteText = properties.routes[selectedIndex]?.text ?? "";

  useEffect(() => {
    localStorage.setItem("selectedIndexSidebarIndex", String(selectedIndex));
  }, [selectedIndex]);

  useEffect(() => {
    localStorage.setItem("sidebarisExpanded", String(isExpanded));
  }, [isExpanded]);

  const location = useLocation();

  useEffect(() => {
    const routeIndex = properties.routes.findIndex((route) => route.route === location.pathname);
    if (routeIndex !== -1 && routeIndex !== selectedIndex) {
      setSelectedIndex(routeIndex);
    }
  }, [location.pathname, properties.routes, selectedIndex]);

  const handleClick = React.useCallback((): void => {
    setIsExpanded((previous) => !previous);
  }, []);

  const onSelect = React.useCallback(
    (e: DrawerSelectEvent): void => {
      const selectedRoute = properties.routes[e.itemIndex]?.route;
      if (selectedRoute) {
        navigate(selectedRoute);
      }
      setSelectedIndex(e.itemIndex);
    },
    [navigate, properties.routes],
  );

  const onMenuItemSelect = React.useCallback(
    (event: MenuSelectEvent): void => {
      const text = event.item.text; // Kendo MenuItem supports an optional `text` prop
      if (text === "Logout") {
        sessionStorage.setItem(logOutInProgress, "true");
        setSessionState(null, null, null);
        try {
          authService.signOut();
        } catch (error) {
          // keep this broad; signOut implementation may vary
          console.warn("Sign out failed:", error);
        }
        navigate("/", { replace: true });
      } else if (text === "My Profile") {
        setShowProfileWindow(true);
      }
    },
    [authService, navigate, setSessionState],
  );

  const renderMenuItem = React.useCallback(
    () => (
      <span className="username">
        <Avatar rounded="full" type="icon" className="user-avatar">
          <SvgIcon icon={userIcon} />
        </Avatar>
        {username}
      </span>
    ),
    [username],
  );

  return (
    <div className="main-container">
      <div className="header-toolbar">
        <div className="flex-center">
          <Button className="header-button" themeColor={"primary"} svgIcon={menuIcon} onClick={handleClick} />
          <span className="flex-center">
            <img src="/tab_omicsFlowLogo_RGB2_16x16.svg" className="header-logo" alt="OmicsFlow logo" />
            <div className="header-title">OmicsFlow</div>
            <div className="header-subtitle">- {selectedRouteText}</div>
          </span>
        </div>

        <div className="flex-center">
          <Menu onSelect={onMenuItemSelect}>
            <MenuItem render={renderMenuItem}>
              <MenuItem svgIcon={userIcon} text="My Profile" />
              <MenuItem svgIcon={logoutIcon} text="Logout" />
            </MenuItem>
          </Menu>
          <div className="push-left">&nbsp;</div>
        </div>
      </div>

      <Drawer
        className="main-container"
        expanded={isExpanded}
        position={"start"}
        mode={"push"}
        mini={true}
        items={properties.routes.map((item, index) => ({
          ...item,
          selected: index === selectedIndex,
        }))}
        onSelect={onSelect}
      >
        <DrawerContent className="drawer-content">{properties.routes[selectedIndex]?.element ?? properties.children}</DrawerContent>
      </Drawer>

      {showProfileWindow && <ProfileWindow username={username} onClose={() => setShowProfileWindow(false)} />}
    </div>
  );
};

export { Sidebar };
